import React from "react";
import Box from "@mui/material/Box";

const ViewAllBidsExportExcel = ({ groupedWorkItems, groupedData, storedUser }) => {
  return (
    <Box sx={{ display: "flex", overflowX: "auto" }}>
      <table
        style={{
          borderCollapse: "collapse",
          minWidth: "100%",
          textAlign: "center",
          border: "1px solid #ccc",
        }}
      >
        <thead>
          {/* First Row: Vendor Names */}
          <tr>
            <th
              colSpan={5}
              style={{
                padding: "9px",
                borderBottom: "1px solid #ccc",
                borderRight: "1px solid #ccc",
                background: "#f9f9f9",
              }}
            >
              Work Items
            </th>
            {Object.entries(groupedData).map(([vendor, bids]) => (
              <th
                key={vendor}
                colSpan={bids.length * 2}
                style={{
                  padding: "9px",
                  borderBottom: "1px solid #ccc",
                  borderRight: "1px solid #ccc",
                }}
              >
                {vendor}
              </th>
            ))}
          </tr>

          {/* Second Row: Bid Numbers */}
          <tr>
            <th style={{ width: "150px" }}>Item Name</th>
            <th style={{ width: "240px" }}>Item Description</th>
            <th style={{ width: "100px" }}>UOM</th>
            <th style={{ width: "100px" }}>Quantity</th>
            <th style={{ width: "100px" }}>Budgeted Rates</th>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((_, i) => (
                <th
                  key={`${vendor}-bid-${i}`}
                  colSpan={2}
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #ccc",
                    borderRight: "1px solid #ccc",
                  }}
                >
                  Bid {i + 1}
                </th>
              ))
            )}
          </tr>

          {/* Third Row: Rate & Total */}
          <tr>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((_, i) => (
                <React.Fragment key={`${vendor}-headers-${i}`}>
                  <th
                    style={{
                      width: "100px",
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    Rate (₹)
                  </th>
                  <th
                    style={{
                      width: "100px",
                      borderTop: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    Total (₹)
                  </th>
                </React.Fragment>
              ))
            )}
          </tr>
        </thead>

        <tbody>
          {Object.entries(groupedWorkItems).map(
            ([section, items], sectionIndex) => (
              <React.Fragment key={sectionIndex}>
                {/* Section Title Row */}
                <tr style={{ fontWeight: "bold", background: "#f1f1f1" }}>
                  <td colSpan={4} style={{ textAlign: "left", padding: "10px" }}>
                    Section: {section}
                  </td>
                  <td style={{ textAlign: "right", padding: "10px" }}>
                    {(() => {
                      const sectionTotal = items.reduce(
                        (sum, si) => sum + ((si.quantity * si.rate) || 0),
                        0
                      );
                      return new Intl.NumberFormat("en-IN", {
                        style: "decimal",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(sectionTotal);
                    })()}
                  </td>
                  {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                    bids.map((bid, i) => {
                      const sectionItems = bid.bidWorkItems.filter((bi) =>
                        items.find((wi) => wi.itemName === bi.itemName)
                      );
                      const sectionTotal = sectionItems.reduce(
                        (sum, si) => sum + ((si.quantity * si.rate) || 0),
                        0
                      );
                      return (
                        <td
                          key={`${vendor}-sectionTotal-${i}-${sectionIndex}`}
                          colSpan={2}
                          style={{ textAlign: "right", padding: "10px" }}
                        >
                          {new Intl.NumberFormat("en-IN", {
                            style: "decimal",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(sectionTotal)}
                        </td>
                      );
                    })
                  )}
                </tr>

                {/* Item Rows */}
                {items.map((item, rowIndex) => (
                  <tr key={rowIndex}>
                    <td style={{ borderTop: "1px solid #ccc", padding: "8px" }}>
                      {item.itemName}
                    </td>
                    <td style={{ borderTop: "1px solid #ccc", padding: "8px" }}>
                      {item.description}
                    </td>
                    <td style={{ borderTop: "1px solid #ccc" }}>{item.unit}</td>
                    <td style={{ borderTop: "1px solid #ccc" }}>
                      {item.quantity}
                    </td>
                    <td style={{ borderTop: "1px solid #ccc" }}>
                      {item.rate}
                    </td>
                    {Object.entries(groupedData).flatMap(([vendor, bids]) =>
                      bids.map((bid, i) => {
                        const matchedItem = bid.bidWorkItems.find(
                          (bi) =>
                            bi.itemName === item.itemName &&
                            bi.description === item.description &&
                            bi.quantity === item.quantity &&
                            bi.unit === item.unit
                        );
                        return (
                          <React.Fragment key={`${vendor}-item-${i}-${rowIndex}`}>
                            <td style={{ borderTop: "1px solid #ccc" }}>
                              {matchedItem?.rate || ""}
                            </td>
                            <td style={{ borderTop: "1px solid #ccc" }}>
                              {(matchedItem?.quantity * matchedItem?.rate) || ""}
                            </td>
                          </React.Fragment>
                        );
                      })
                    )}
                  </tr>
                ))}
              </React.Fragment>
            )
          )}

          {/* Grand Total Row */}
          <tr style={{ fontWeight: "bold", borderTop: "2px solid #000" }}>
            <td colSpan={4} style={{ textAlign: "right", padding: "10px" }}>
              Grand Total (₹)
            </td>
            <td style={{ textAlign: "right", padding: "10px" }}>
              {new Intl.NumberFormat("en-IN", {
                style: "decimal",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(
                Object.entries(groupedWorkItems).reduce((grand, [section, items]) => {
                  const sectionTotal = items.reduce(
                    (sum, si) => sum + ((si.quantity * si.rate) || 0),
                    0
                  );
                  return grand + sectionTotal;
                }, 0)
              )}
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => {
                const grandTotal = bid.bidWorkItems.reduce(
                  (acc, item) => acc + ((item.quantity * item.rate) || 0),
                  0
                );
                return (
                  <td
                    key={`${vendor}-grand-${i}`}
                    colSpan={2}
                    style={{ textAlign: "right", padding: "10px" }}
                  >
                    {new Intl.NumberFormat("en-IN", {
                          style: "decimal",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(grandTotal)}
                  </td>
                );
              })
            )}
          </tr>

          {/* Current Status Row */}
          <tr>
            <td colSpan={5} style={{ fontWeight: "bold" }}>
              Current Status
            </td>
            {Object.entries(groupedData).flatMap(([vendor, bids]) =>
              bids.map((bid, i) => (
                <td
                  key={`${vendor}-status-${i}`}
                  colSpan={2}
                  style={{
                    borderTop: "1px solid #ccc",
                    height: "40px",
                  }}
                >
                  {storedUser.userRole[0] === "PURCHASE_HEAD" &&
                    (bid.bidStatus === "AWARDED" ||
                    bid.bidStatus === "SHORTLISTED"
                      ? "APPROVED"
                      : bid.bidStatus === "TECHNICALLY_QUALIFIED"
                      ? "REVIEW"
                      : bid.bidStatus === "REBID"
                      ? "REBID REQUESTED"
                      : bid.bidStatus)}
                  {bid.awardee1?.userRoleName === storedUser.userRole[0] &&
                    (bid.bidStatus === "AWARDED" ||
                    bid.bidStatus === "APPROVED"
                      ? "APPROVED"
                      : bid.bidStatus === "SHORTLISTED"
                      ? "REVIEW"
                      : bid.bidStatus === "REBID"
                      ? "REBID REQUESTED"
                      : bid.bidStatus)}
                  {bid.awardee2?.userRoleName === storedUser.userRole[0] &&
                    (bid.bidStatus === "AWARDED"
                      ? "APPROVED"
                      : bid.bidStatus === "APPROVED"
                      ? "REVIEW"
                      : bid.bidStatus === "REBID"
                      ? "REBID REQUESTED"
                      : bid.bidStatus)}
                </td>
              ))
            )}
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

export default ViewAllBidsExportExcel;